import { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
  useTheme,
} from "@mui/material";

const STRATEGY_LABEL_COLORS = ["primary", "warning"];

/**
 * StrategyCard
 *
 * @param {object}   strategy           - Strategy data object
 * @param {number}   index              - Position in the strategies array (0 = main, 1+ = compare)
 * @param {Array}    allOptions         - All available strategies for the swap autocomplete
 * @param {boolean}  isOptionsLoading   - Loading state for options
 * @param {Array}    activeStrategies   - All currently active strategies (used to filter out already-selected ones)
 * @param {function} onSwap             - Called with newSlug when user selects a replacement strategy
 */
const StrategyCard = ({ strategy, index, allOptions, isOptionsLoading, activeStrategies, onSwap }) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");

  const labelColor = STRATEGY_LABEL_COLORS[index] ?? "default";
  const isCompare = index > 0;

  const filteredOptions = allOptions.filter(
    (option) => !activeStrategies.some((active) => active.idStrategy === option.strategyId)
  );

  const handleSwapChange = (_, selected) => {
    setInputValue("");
    if (selected) onSwap(selected.slug);
  };

  return (
    <Card
      sx={{
        p: 3,
        flex: 1,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Typography
          variant="overline"
          color={`${labelColor}.main`}
          fontWeight={700}
          letterSpacing={1.5}
        >
          {index === 0 ? "STRATEGY A" : `STRATEGY ${String.fromCharCode(65 + index)}`}
        </Typography>

        {isCompare && (
          <Autocomplete
            options={filteredOptions}
            getOptionLabel={(option) => option.name || ""}
            getOptionKey={(option) => option.strategyId}
            value={null}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
              if (reason !== "reset") setInputValue(newInputValue);
            }}
            onChange={handleSwapChange}
            loading={isOptionsLoading}
            size="small"
            sx={{ width: 200 }}
            renderOption={(props, option) => (
              <li {...props}>
                <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={strategy?.title ?? "Select strategy"}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isOptionsLoading && <CircularProgress size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
      </Box>

      {strategy ? (
        <>
          <Typography variant="h3" gutterBottom>
            {strategy.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {strategy.description}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {strategy.tags?.map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
            {strategy.categories?.map((category) => (
              <Chip key={category} label={category} size="small" color={labelColor} variant="outlined" />
            ))}
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      )}
    </Card>
  );
};

export default StrategyCard;
